import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { HistoryEntryDto, PlayerStatsDto } from '@chromarble/shared-types';
import { roomTypeLabel } from '@chromarble/crew-adapter';
import { ErrorNote, Screen, formatDateTime, formatScore, pct } from '../components/ui';
import { api } from '../services/api';
import { useSession } from '../services/session';

const OUTCOME: Record<string, string> = { WIN: '승', DRAW: '무', LOSS: '패' };

export default function HistoryPage() {
  const user = useSession((s) => s.user)!;
  const [stats, setStats] = useState<PlayerStatsDto | null>(null);
  const [entries, setEntries] = useState<HistoryEntryDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.stats(user.id).then(setStats, setError);
    api.history(user.id).then((h) => {
      setEntries(h.entries);
      setCursor(h.nextCursor);
      setLoaded(true);
    }, setError);
  }, [user.id]);

  const more = async () => {
    try {
      const h = await api.history(user.id, cursor);
      setEntries((e) => [...e, ...h.entries]);
      setCursor(h.nextCursor);
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Screen title="내 전적" back="/">
      {stats && (
        <section className="card stats" data-testid="my-stats">
          <div>
            <span className="label">총 경기</span>
            <strong data-testid="stat-matches">{stats.totalMatches}</strong>
          </div>
          <div>
            <span className="label">우승</span>
            <strong data-testid="stat-wins">{stats.totalWins}</strong>
          </div>
          <div>
            <span className="label">승률</span>
            <strong>{pct(stats.winRate)}</strong>
          </div>
          <div>
            <span className="label">최고 점수</span>
            <strong>{formatScore(stats.bestScore)}</strong>
          </div>
          <div>
            <span className="label">평균 점수</span>
            <strong>{formatScore(stats.averageScore)}</strong>
          </div>
          <div>
            <span className="label">무/패</span>
            <strong>
              {stats.totalDraws}/{stats.totalLosses}
            </strong>
          </div>
        </section>
      )}
      <h3>최근 경기</h3>
      {loaded && entries.length === 0 && <p className="muted">아직 완료한 경기가 없습니다.</p>}
      <ul className="list history" data-testid="history">
        {entries.map((e) => (
          <li key={e.matchId}>
            <Link to={`/matches/${e.matchId}/result`}>
              <span className={`outcome ${e.outcome.toLowerCase()}`}>{OUTCOME[e.outcome]}</span>
              <span className="name">
                {e.roomTitle} <span className="muted small">· {roomTypeLabel(e.roomType)}</span>
              </span>
              <span className="score">
                {e.finalRank}/{e.playerCount}위 · {formatScore(e.finalScore)}점
              </span>
              <span className="muted small">{formatDateTime(e.finalizedAt)}</span>
            </Link>
          </li>
        ))}
      </ul>
      {cursor && (
        <button className="btn secondary" onClick={more}>
          더 보기
        </button>
      )}
      <ErrorNote error={error} />
    </Screen>
  );
}
